import React from "react";
import assign from "lodash/assign";
import get from "lodash/get";
import set from "lodash/set";
import marked from "marked";
import moment from "moment";
import Select, { Creatable } from "react-select";
import {
  Checkbox,
  TextField,
  RaisedButton,
  RadioButtonGroup,
  RadioButton
} from "material-ui";
import { colors } from "material-ui/styles";
import { Link } from "react-router";

import config from "../config";
import UserStore from "../stores/UserStore";

import WorkingIndicator from "./partials/working-indicator.jsx";
import Label from "./partials/label.jsx";
import LinkLike from "./partials/link-like.jsx";
import BackButton from "./partials/back-button.jsx";
import SelectField from "./partials/select-field.jsx";
import * as util from "../util";

import InfoNotice from "./partials/info-notice.jsx";
import SelectWells from "./partials/select-wells.jsx";
import ListTargetBuilder from "./partials/list-target-builder.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import ExternalSyncActions from "../actions/ExternalSyncActions";
import ExternalSyncStore from "../stores/ExternalSyncStore";
import EventActions from "../actions/EventActions";
import EventBucketStore from "../stores/EventBucketStore";
import EventStore from "../stores/EventStore";
import PassBucketActions from "../actions/PassBucketActions";
import PassBucketStore from "../stores/PassBucketStore";
import CouponActions from "../actions/CouponActions";
import CouponStore from "../stores/CouponStore";
import TagActions from "../actions/TagActions";
import TagStore from "../stores/TagStore";

const PROVIDERS = {
  mailchimp: { display_name: "MailChimp" },
  eventival: { display_name: "Eventival" }
};

class ExternalSync extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      externalSync: null,
      dirty: false,
      isWorking: false,
      values: {},
      lastUpdated: null
    };

    this.onChangeValue = this.onChangeValue.bind(this);
    this.save = this.save.bind(this);
    this.delete = this.delete.bind(this);
    this.forceSync = this.forceSync.bind(this);
    this.onConnectClick = this.onConnectClick.bind(this);
  }

  static getStores() {
    return [
      ExternalSyncStore,
      EventStore,
      PassBucketStore,
      CouponStore,
      TagStore
    ];
  }

  static getPropsFromStores() {
    const state = ExternalSyncStore.getState();
    return {
      getExternalSyncById: ExternalSyncStore.getExternalSyncById,
      ...state
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  onChangeValue(id, value) {
    // If nothing is actually changed, don't do anything
    if (get(this.state.values, id) === value) {
      return;
    }

    // Update mailchimp segments proactively
    if (this.state.values.provider === "mailchimp" && id === "result.list") {
      ExternalSyncActions.fetchMailchimpSegments.defer(value);
    }

    this.state.dirty = true;
    set(this.state.values, id, value);
    this.setState(this.state);
  }

  save() {
    var values = assign({}, this.state.values);
    this.setState({
      isWorking: true,
      lastUpdated: this.state.externalSync.updated_at
    });

    if (
      values.provider === "mailchimp" &&
      values.result &&
      values.result.list
    ) {
      values.description = `MailChimp ${
        values.result.mode ? values.result.mode + " " : ""
      }sync to "${
        (
          this.props.providerStatus.providers.mailchimp.lists.find(
            l => l.id === values.result.list
          ) || { name: "Unknown list" }
        ).name
      }"`;
    }

    if (
      values.provider === "eventival" &&
      values.result &&
      values.result.project
    ) {
      const selected =
        this.props.providerStatus.providers.eventival.projects.find(
          p => p.id === values.result.project
        ) || { name: "Unknown Project" };
      values.description = `Eventival sync from "${selected.name}"${
        values.result.edition ? `, ${values.result.edition} edition` : ""
      }`;
    }

    ExternalSyncActions.edit(this.props.params.externalSyncId, values);
  }

  forceSync() {
    window.scrollTo(0, 0);
    let bucket = this.state.externalSync.event_bucket;
    this.setState({
      isWorking: true,
      lastUpdated: this.state.externalSync.updated_at
    });
    ExternalSyncActions.forceSync(this.props.params.externalSyncId);
  }

  onConnectClick(e) {
    if (this.state.values.provider === "eventival") {
      e.preventDefault();
      Intercom(
        "showNewMessage",
        "I'm ready to connect my Eventival account. My Eventival organization name is: "
      );
    }
  }

  delete() {
    let bucket = this.state.externalSync.event_bucket;
    if (
      window.confirm(
        "Are you sure? No further syncs will be performed. (Note: By deleting the external sync, existing external records will not be removed.)"
      )
    ) {
      ExternalSyncActions.delete(this.props.params.externalSyncId);
      this.context.router.push(`/event_buckets/${bucket}/external_syncs`);
    }
  }

  componentDidMount() {
    this.componentWillReceiveProps(this.props);
    ExternalSyncActions.fetchProviderStatus();
    PassBucketActions.fetchAllForEventBucket(this.props.params.eventBucketId);
    EventActions.fetchAllForBucket(this.props.params.eventBucketId);
    CouponActions.fetchAllForBucket(this.props.params.eventBucketId);
    TagActions.fetchAllForBucket(this.props.params.eventBucketId);
  }

  componentWillReceiveProps(newProps) {
    let externalSync = newProps.getExternalSyncById(
      newProps.params.externalSyncId
    );

    if (!this.state.isFetchingExternalSync && !externalSync) {
      ExternalSyncActions.clearErrors();
      ExternalSyncActions.fetchOne(newProps.params.externalSyncId);
      this.setState({ isFetchingExternalSync: true });
    } else if (externalSync) {
      this.setState({ isFetchingExternalSync: false });
    }

    if (this.state.isWorking && !newProps.isEditing) {
      this.setState({ isWorking: false });
    }

    if (
      Object.keys(this.state.values).length === 0 ||
      this.state.externalSync.id !== newProps.params.externalSyncId ||
      (this.state.isWorking &&
        externalSync.updated_at !== this.state.lastUpdated)
    ) {
      if (
        externalSync &&
        externalSync.provider === "mailchimp" &&
        externalSync.result.list
      ) {
        ExternalSyncActions.fetchMailchimpSegments.defer(
          externalSync.result.list
        );
      }

      this.setState({
        externalSync,
        isWorking: false,
        dirty: false,
        values: externalSync
          ? {
              label: externalSync.label,
              provider: externalSync.provider,
              query: externalSync.query,
              result: externalSync.result
            }
          : {}
      });
    }
  }

  getStyles() {
    return {
      actions: {
        marginTop: 20
      },
      error: {
        color: "red",
        marginTop: 10
      }
    };
  }

  render() {
    let styles = this.getStyles(),
      externalSync = this.props.getExternalSyncById(
        this.props.params.externalSyncId
      );

    if (
      !externalSync ||
      !this.state.externalSync ||
      this.state.isFetchingExternalSync ||
      this.state.isWorking ||
      !this.props.hasFetchedProviderStatus
    ) {
      return <WorkingIndicator fullPage={true} />;
    }

    const okayToForceSync = !this.state.dirty;

    const selectedMailchimpSegment = get(this.state, "values.result.segment");

    return (
      <div>
        <div>
          <BackButton
            label="External syncs"
            style={{ marginBottom: 10 }}
            location={`/event_buckets/${this.props.params.eventBucketId}/external_syncs`}
          />
        </div>
        <div>
          <InfoNotice style={{ marginTop: 10 }}>
            Check out our{" "}
            <a
              href="https://help.eventive.org/integrations/create-external-syncs"
              target="_blank"
            >
              guide to external syncs
            </a>
            .
          </InfoNotice>

          <div style={{ marginTop: "1em" }}>
            <TextField
              value={get(this.state, "values.label", "")}
              floatingLabelText="Label"
              floatingLabelFixed={true}
              style={{ marginTop: -12, marginBottom: -6, width: "100%" }}
              onChange={e => this.onChangeValue("label", e.target.value)}
            />
          </div>
          <div style={{ marginTop: "1em" }}>
            <Label>External provider</Label>
            <SelectWells
              style={{ maxWidth: 450 }}
              options={[
                {
                  value: "mailchimp",
                  icon: () => (
                    <img
                      src="https://storage.googleapis.com/eventive-co/mailchimp-logo.svg"
                      style={{ width: "80%", marginBottom: -8 }}
                    />
                  )
                },
                {
                  value: "eventival",
                  icon: () => (
                    <img
                      src="https://storage.googleapis.com/eventive-co/eventival-dark.png"
                      style={{ width: "60%", verticalAlign: "middle" }}
                    />
                  )
                }
              ]}
              onChange={v => this.onChangeValue("provider", v)}
              value={this.state.values.provider}
            />
          </div>
          <div style={{ marginTop: 10 }}>
            {this.props.providerStatus.providers[this.state.values.provider] ? (
              <div
                style={{
                  display: "inline-block",
                  borderRadius: 8,
                  padding: "7px 12px",
                  backgroundColor: "#4CAF50",
                  color: "#fff"
                }}
              >
                {PROVIDERS[this.state.values.provider].display_name} account
                connected & ready for sync.
              </div>
            ) : (
              <div
                style={{
                  display: "inline-block",
                  borderRadius: 8,
                  padding: "7px 12px",
                  backgroundColor: colors.red600,
                  color: "#fff"
                }}
              >
                No {PROVIDERS[this.state.values.provider].display_name} account
                connected.{" "}
                <a
                  onClick={this.onConnectClick}
                  href={
                    config.endpoint +
                    `auth/${
                      this.state.values.provider
                    }?${UserStore.makeApiKeyQueryString()}&redirect=${encodeURIComponent(
                      window.location.href
                    )}`
                  }
                  style={{ color: "#fff", textDecoration: "underline" }}
                >
                  Connect one to continue.
                </a>
              </div>
            )}
          </div>
          {this.state.values.provider === "mailchimp" && (
            <div style={{ marginTop: 20 }}>
              <ListTargetBuilder
                label="Target customers to sync"
                eventBucketId={this.props.params.eventBucketId}
                stores={{
                  EventStore,
                  CouponStore,
                  PassBucketStore,
                  EventBucketStore,
                  TagStore
                }}
                query={get(this.state, "values.query", {})}
                onChange={this.onChangeValue}
                allLabel="Sync all attendees (pass & ticket holders)"
                standaloneLabel="Sync all standalone ticketholders (excluding passholders)"
              />
            </div>
          )}
          {this.state.values.provider === "eventival" && (
            <div style={{ marginTop: 20 }}>
              <Label>Eventival sync information</Label>
              <div style={{ maxWidth: 500 }}>
                When you synchronize with an Eventival project, we'll
                automatically copy events, films, and/or passes from your
                Eventival database. Existing events/films/passes will not be
                affected.
              </div>

              <div
                style={
                  this.props.providerStatus.providers.eventival
                    ? {}
                    : { pointerEvents: "none", opacity: 0.5 }
                }
              >
                <SelectField
                  value={get(this.state, "values.result.project", null)}
                  label="Eventival source project"
                  placeholder="Select a project..."
                  style={{ marginTop: 20, maxWidth: 300 }}
                  options={
                    this.props.providerStatus.providers.eventival
                      ? this.props.providerStatus.providers.eventival.projects.map(
                          l => ({ label: l.name, value: l.id })
                        )
                      : []
                  }
                  onChange={selected =>
                    this.onChangeValue(
                      "result.project",
                      selected ? selected.value : null
                    )
                  }
                />

                <SelectField
                  value={get(this.state, "values.result.edition", null)}
                  label="Eventival source edition"
                  placeholder="Select an edition..."
                  style={{ marginTop: 20, maxWidth: 300 }}
                  options={
                    this.props.providerStatus.providers.eventival
                      ? (
                          this.props.providerStatus.providers.eventival.projects.find(
                            p =>
                              p.id ===
                              get(this.state, "values.result.project", null)
                          ) || { editions: [] }
                        ).editions.map(e => ({ label: e, value: e }))
                      : []
                  }
                  onChange={selected =>
                    this.onChangeValue(
                      "result.edition",
                      selected ? selected.value : null
                    )
                  }
                />

                <div>
                  <Label style={{ marginTop: 20 }}>
                    Eventival sync options
                  </Label>
                  <Checkbox
                    label="Sync films from Eventival"
                    style={{ width: 300 }}
                    checked={get(this.state, "values.result.films")}
                    onCheck={(e, checked) => {
                      this.onChangeValue(
                        "result.films",
                        !get(this.state, "values.result.films")
                      );
                    }}
                  />
                  <Checkbox
                    label="Sync screenings from Eventival"
                    style={{ width: 300 }}
                    checked={get(this.state, "values.result.screenings")}
                    onCheck={(e, checked) => {
                      this.onChangeValue(
                        "result.screenings",
                        !get(this.state, "values.result.screenings")
                      );
                    }}
                  />
                  <Checkbox
                    label="Sync other events from Eventival"
                    style={{ width: 300 }}
                    checked={get(this.state, "values.result.events")}
                    onCheck={(e, checked) => {
                      this.onChangeValue(
                        "result.events",
                        !get(this.state, "values.result.events")
                      );
                    }}
                  />
                  <Checkbox
                    label="Sync passes from Eventival"
                    style={{ width: 300 }}
                    checked={get(this.state, "values.result.passes")}
                    onCheck={(e, checked) => {
                      this.onChangeValue(
                        "result.passes",
                        !get(this.state, "values.result.passes")
                      );
                    }}
                  />

                  <Checkbox
                    label="Push Eventive pass orders to Eventival"
                    style={{ width: 300 }}
                    checked={get(this.state, "values.result.passes_push")}
                    onCheck={(e, checked) => {
                      this.onChangeValue(
                        "result.passes_push",
                        !get(this.state, "values.result.passes_push")
                      );
                    }}
                  />

                  {get(this.state, "values.result.passes_push") ? (
                    <p>
                      Note: pass push sync requires an{" "}
                      <strong>exact match</strong> between Eventive pass bucket
                      name and Eventival badge type. If there is not an
                      Eventival badge type with the name matching exactly, no
                      Eventival badge will be created.{" "}
                      <i>
                        Eventival only allows one badge per person. If someone
                        purchases multiple Eventive passes, they will receive
                        only one account with one badge.
                      </i>
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          )}
          {this.state.values.provider === "mailchimp" ? (
            <div
              style={
                this.props.providerStatus.providers.mailchimp
                  ? {}
                  : { pointerEvents: "none", opacity: 0.5 }
              }
            >
              <div style={{ marginTop: 20 }}>
                <div>
                  <SelectField
                    value={get(this.state, "values.result.list", null)}
                    label="MailChimp destination list"
                    placeholder="Select a list..."
                    style={{ maxWidth: 300 }}
                    options={
                      this.props.providerStatus.providers.mailchimp
                        ? this.props.providerStatus.providers.mailchimp.lists.map(
                            l => ({ label: l.name, value: l.id })
                          )
                        : []
                    }
                    onChange={selected =>
                      this.onChangeValue(
                        "result.list",
                        selected ? selected.value : null
                      )
                    }
                  />
                </div>
                <div style={{ marginTop: 5 }}>
                  Or{" "}
                  <a
                    target="_blank"
                    href={
                      this.props.providerStatus.providers.mailchimp &&
                      this.props.providerStatus.providers.mailchimp.new_list_url
                    }
                  >
                    create a new list
                  </a>
                  , and then{" "}
                  <LinkLike
                    onTouchTap={() => ExternalSyncActions.fetchProviderStatus()}
                  >
                    refresh options
                  </LinkLike>
                </div>
              </div>
              {get(this.state, "values.result.list") ? (
                <div style={{ marginTop: 15 }}>
                  <Checkbox
                    label="Update static list segment"
                    style={{ width: 250 }}
                    checked={
                      this.state.showSegmentEditor ||
                      get(this.state, "values.result.segment")
                    }
                    onCheck={(e, checked) => {
                      if (
                        !this.state.showSegmentEditor &&
                        !get(this.state, "values.result.segment")
                      ) {
                        this.setState({ showSegmentEditor: true });
                      } else {
                        this.setState({ showSegmentEditor: false }, () => {
                          this.onChangeValue("result.segment", null);
                        });
                      }
                    }}
                  />
                  {this.state.showSegmentEditor ||
                  get(this.state, "values.result.segment") ? (
                    <div style={{ maxWidth: 300, marginTop: 10 }}>
                      <Creatable
                        value={get(this.state, "values.result.segment", null)}
                        label="MailChimp destination segment"
                        placeholder="Select or type to create a segment..."
                        options={(
                          this.props.mailchimpSegments[
                            this.state.values.result.list
                          ] || []
                        )
                          .map(s => ({ label: s.name, value: s.id }))
                          .concat(
                            !(
                              this.props.mailchimpSegments[
                                this.state.values.result.list
                              ] || []
                            ).find(s => s.id === selectedMailchimpSegment) &&
                              selectedMailchimpSegment
                              ? [
                                  {
                                    label: `${selectedMailchimpSegment} [will be created on sync]`,
                                    value: selectedMailchimpSegment
                                  }
                                ]
                              : []
                          )}
                        promptTextCreator={s => `Create new segment "${s}"`}
                        onChange={selected =>
                          this.onChangeValue(
                            "result.segment",
                            selected ? selected.value : null
                          )
                        }
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div style={{ marginTop: 20 }}>
                <Label>MailChimp sync mode</Label>
                <RadioButtonGroup
                  name="mailchimp_sync_mode"
                  valueSelected={get(this.state, "values.result.mode", null)}
                  onChange={(e, v) => this.onChangeValue("result.mode", v)}
                >
                  <RadioButton
                    value="push"
                    label="Push (automatically add all targeted Eventive customers, but never remove subscribers from MailChimp list)"
                  />
                  <RadioButton
                    value="active"
                    label="Active sync (automatically add all targeted Eventive customers, and actively remove non-matching subscribers from MailChimp list)"
                  />
                </RadioButtonGroup>
              </div>
            </div>
          ) : null}
          <div style={{ marginTop: 20 }}>
            <Label>Sync status</Label>
            <div>
              <strong>
                {!externalSync.last_synced_at
                  ? "Not yet synced. Syncs run hourly."
                  : `Last synced ${moment(externalSync.last_synced_at).format(
                      "lll"
                    )}`}{" "}
                {externalSync.last_sync_details.error_message ? (
                  <span style={{ color: colors.red600 }}>
                    Failed: {externalSync.last_sync_details.error_message}
                  </span>
                ) : (
                  <span style={{ color: "#4CAF50" }}>
                    {externalSync.last_sync_details.message}
                  </span>
                )}
              </strong>
            </div>
            <div>
              {!okayToForceSync
                ? "To force an immediate sync, first save changes."
                : "Force sync immediately using the button below."}
            </div>
          </div>
          {this.props.errorMessage ? (
            <div style={styles.error}>{this.props.errorMessage}</div>
          ) : null}
          <div style={styles.actions}>
            <RaisedButton
              label={this.state.dirty ? "Save changes" : "No changes to save"}
              primary={true}
              disabled={!this.state.dirty}
              onClick={this.save}
            />
            <RaisedButton
              label="Delete external sync"
              onClick={this.delete}
              style={{ marginLeft: 7 }}
            />
            <RaisedButton
              label="Force sync"
              disabled={!okayToForceSync}
              onClick={this.forceSync}
              style={{ marginLeft: 7 }}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default connectToStores(ExternalSync);



// WEBPACK FOOTER //
// ./src/app/components/external-sync.jsx